import {HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {
  APP_LANGUAGE_HEADER_KEY,
  APP_NAME_HEADER_KEY,
  AppName,
  jsonToObject,
  UTM_PARAMS_HEADER_KEY,
  UtmParams,
  VERSION_HASH_HEADER_KEY,
} from '@emma-labs/shared/typescript';
import {Observable, throwError} from 'rxjs';
import {LanguageCache} from './language-cache';

export type ContentType =
  | 'application/json'
  | 'application/x-www-form-urlencoded'
  | 'application/octet-stream'
  | 'text/plain'
  | 'text/csv'
  | 'application/pdf'
  | 'multipart/mixed'
  | 'multipart/form-data';

export class HttpOptionsBuilder {
  private headers = new HttpHeaders({});
  private params = new HttpParams();

  constructor() {
    this.appendAppLanguageHeader();
  }

  private appendAppLanguageHeader() {
    if (LanguageCache.isLanguagePresent()) {
      this.withHeader(APP_LANGUAGE_HEADER_KEY, LanguageCache.get());
    }
  }

  withContentType(contentType: ContentType): HttpOptionsBuilder {
    return this.withHeader('Content-Type', contentType);
  }

  withAccept(contentType: ContentType): HttpOptionsBuilder {
    return this.withHeader('Accept', contentType);
  }

  withApiKey(apiKey: string): HttpOptionsBuilder {
    return this.withHeader('X-Api-Key', apiKey);
  }

  withHeader(key: string, value: string | string[]): HttpOptionsBuilder {
    this.headers = this.headers.set(key, value);
    return this;
  }

  withSearchParam(key: string, value: string | string[]): HttpOptionsBuilder {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        this.params = this.params.append(key, v);
      });
    } else {
      this.params = this.params.append(key, value);
    }
    return this;
  }

  buildOptions(): {headers: HttpHeaders; params: HttpParams; observe: 'response'} {
    return {
      headers: this.headers,
      observe: 'response',
      params: this.params,
    };
  }
}

export const toUriEncodedBody = (params: {[key: string]: string}): string => {
  const pairs: string[] = [];
  for (const key in params) {
    // eslint-disable-next-line no-prototype-builtins
    if (params.hasOwnProperty(key)) {
      const value = params[key];
      // eslint-disable-next-line
      if (value !== null && value !== undefined) {
        const encodedValue = encodeURIComponent(value);
        pairs.push(key + '=' + encodedValue);
      }
    }
  }

  return pairs.join('&');
};

export interface Files {
  [name: string]: Blob;
}

export interface HeaderOptions {
  appName?: AppName;
  versionHash?: string;
  utmParams?: UtmParams;
}

export const defaultOptions = (sessionId: string, headerOptions?: HeaderOptions): HttpOptionsBuilder => {
  const builder = new HttpOptionsBuilder()
    .withContentType('application/json')
    .withAccept('application/json')
    .withApiKey(sessionId);

  if (headerOptions?.appName) {
    builder.withHeader(APP_NAME_HEADER_KEY, headerOptions.appName);
  }

  if (headerOptions?.versionHash) {
    builder.withHeader(VERSION_HASH_HEADER_KEY, headerOptions.versionHash);
  }

  if (headerOptions?.utmParams) {
    builder.withHeader(UTM_PARAMS_HEADER_KEY, JSON.stringify(headerOptions.utmParams));
  }

  return builder;
};

export const pdfOptions = (sessionId: string, source?: AppName): HttpOptionsBuilder => {
  const builder = new HttpOptionsBuilder()
    .withContentType('application/pdf')
    .withAccept('application/pdf')
    .withApiKey(sessionId);
  if (source) {
    builder.withHeader(APP_NAME_HEADER_KEY, source);
  }
  return builder;
};

export const postPdfOptions = (sessionId: string): HttpOptionsBuilder =>
  new HttpOptionsBuilder().withContentType('application/pdf').withApiKey(sessionId);

export const UNSUPPORTED_CHARACTERS = ['\\', '{', '^', '}', '%', '`', ']', '>', '<', '~', '#', '|'];

export const responseToObject = <T>(response: HttpResponse<string>): T => {
  if (response.body) {
    return jsonToObject(response.body);
  }
  throw new Error('Empty HTTP response body');
};

export const handleHttpError = (error: HttpErrorResponse): Observable<never> => throwError(error.error);

export function appendSearchParam(url: URL, param: string, value: string): void {
  url.searchParams.set(param, url.searchParams.get(param)?.concat(`,${value}`) ?? value);
}
